const ERRORS = {
  NO_PRODUCTS: 'Please select products to add to cart',
  NO_PILLOW_SELECTED: 'Please select your pillow',
  GENERAL_ERROR: 'Error adding products to cart',
  SOLD_OUT: (variantName) => `${variantName} is currently sold out, please choose another pillow`,
};

const TIMEOUTS = {
  MESSAGE_DURATION: 3000,
};

/**
 * @class ProductBundle
 * @description Manages product bundle selection and cart functionality
 */
class ProductBundle {
  constructor() {
    this.bundleForm = document.querySelector('[data-bundle-form]');
    this.addToCartButton = document.querySelector('[data-bundle-atc]');
    this.addToCartText = document.querySelector('[data-bundle-atc-text]');
    this.addToCartSuccess = document.querySelector('[data-bundle-atc-success]');
    this.primaryVariantSelect = document.querySelector('[data-bundle-primary-select]');
    this.allBundleInputs = document.querySelectorAll('[data-bundle-form-input]');

    this.variantImagesCache = new Map();
    this.imagePreloadQueue = [];

    if (!this.addToCartButton) return;

    this.boundHandleAddToCart = this.handleAddToCart.bind(this);
    this.boundSyncVariantSelections = this.syncVariantSelections.bind(this);

    this.init();
  }

  /**
   * @method init
   * @private
   */
  init() {
    this.addToCartButton.addEventListener('click', this.boundHandleAddToCart);

    if (this.primaryVariantSelect) {
      this.primaryVariantSelect.addEventListener('change', this.boundSyncVariantSelections);

      // Trigger initial sync with currently selected variant
      const selectedInput = this.primaryVariantSelect.querySelector('input[type="radio"]:checked');
      if (selectedInput) {
        const initialEvent = new Event('change', { bubbles: true });
        selectedInput.dispatchEvent(initialEvent);
      }
    }

    // Debounce the variant selection updates
    const debouncedUpdateVariants = this.debounce(this.updateVariantSelections.bind(this), 150);
    this.allBundleInputs.forEach((input) => {
      input.addEventListener('change', debouncedUpdateVariants);
    });

    // Trigger initial variant updates
    this.updateVariantSelections({ target: this.primaryVariantSelect });

    // Start prefetching images after initialization
    this.prefetchVariantImages();
  }

  /**
   * @method debounce
   * @private
   */
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  /**
   * @method cleanup
   * @description Remove event listeners
   */
  cleanup() {
    this.addToCartButton?.removeEventListener('click', this.boundHandleAddToCart);
    this.primaryVariantSelect?.removeEventListener('change', this.boundSyncVariantSelections);
  }

  /**
   * @method getBundleSavings
   * @private
   * @description Gets bundle savings amount from variant metafields
   * @param {Object} selectedVariant - The selected variant object
   * @param {Object} metafields - The metafields object for the product
   * @returns {number|null} The bundle savings amount in cents, or null if not found
   */
  getBundleSavings(selectedVariant, metafields) {
    if (
      !selectedVariant?.id ||
      !metafields?.variants?.[selectedVariant.id]?.metafields?.custom?.bundle_savings?.value
    ) {
      return null;
    }

    try {
      const savingsValue = metafields.variants[selectedVariant.id].metafields.custom.bundle_savings.value;
      // Convert to number and ensure it's positive
      const savings = Math.abs(parseFloat(savingsValue));
      return isNaN(savings) ? null : savings;
    } catch (e) {
      console.error('Error parsing bundle savings:', e);
      return null;
    }
  }

  /**
   * @method useParentProduct
   * @private
   * @description Checks if the parent product should be used
   * @returns {boolean} True if the parent product should be used, false otherwise
   */
  useParentProduct() {
    return this.bundleForm.dataset.useParentProduct !== undefined;
  }

  /**
   * @method getParentProduct
   * @private
   * @description Gets the parent product object and selected variant from the bundle form
   * @returns {Object} The parent product object and selected variant
   */
  getParentProduct() {
    const parentProductId = this.bundleForm.dataset.parentProductId;
    const parentProduct = document.querySelector(`[data-product-id="${parentProductId}"]`);

    if (!parentProduct) {
      return { parentProduct: null, selectedVariant: null };
    }

    const selectedVariant = this.bundleForm.querySelector(`[data-bundle-primary-select] input[type="radio"]:checked`);

    if (!selectedVariant) {
      return { parentProduct, selectedVariant: null };
    }

    // Use the same pattern as other products - look for script tag with product JSON
    const productJson = document.querySelector(`script[data-product-json-parent-${parentProductId}]`);
    if (!productJson) {
      return { parentProduct, selectedVariant: null };
    }

    try {
      const productData = JSON.parse(productJson.textContent);
      const selectedOption = selectedVariant.value;

      // Find the variant that matches the selected option
      const matchingVariant = productData.variants.find((variant) => variant.option1 === selectedOption);

      return {
        parentProduct,
        selectedVariant: matchingVariant ? { id: matchingVariant.id, value: selectedOption } : null,
      };
    } catch (error) {
      console.error('Error parsing parent product JSON:', error);
      return { parentProduct, selectedVariant: null };
    }
  }

  /**
   * @method updatePrimaryPrice
   * @private
   * @description Updates the price display for the primary product variant
   * @param {Object} product - The product object
   * @param {Object} selectedVariant - The selected variant object
   * @param {string} priceContainerSelector - Optional selector for price container (defaults to '[data-price-container]')
   * @param {Object} metafields - Optional metafields object for bundle savings
   */
  updatePrimaryPrice(product, selectedVariant, priceContainerSelector = '[data-price-container]', metafields = null) {
    if (!selectedVariant) return;

    const priceContainer = document.querySelector(priceContainerSelector);
    if (!priceContainer) return;

    // Regular product price format - with compare price and savings
    const priceDiv = priceContainer.querySelector('.text-h5');
    if (priceDiv) {
      // Check for bundle savings
      const bundleSavings = this.getBundleSavings(selectedVariant, metafields);

      // Calculate prices
      const originalPrice = parseFloat(selectedVariant.price);
      const comparePrice = selectedVariant.compare_at_price ? parseFloat(selectedVariant.compare_at_price) : null;

      // If bundle savings exist, use original price as compare price and calculate discounted price
      let displayPrice = originalPrice;
      let displayComparePrice = comparePrice;

      if (bundleSavings) {
        displayPrice = Math.max(0, originalPrice - bundleSavings);
        displayComparePrice = originalPrice; // Use original price as the "compare at" price
        priceDiv.insertAdjacentHTML('beforeend', `<s></s>`);
        priceDiv.insertAdjacentHTML('beforeend', `<span class="caption text-brand-02"></span>`);
      }

      // Format prices without cents
      const formattedPrice = `$${Math.floor(displayPrice / 100)}`;
      const formattedComparePrice = displayComparePrice ? `$${Math.floor(displayComparePrice / 100)}` : null;

      // Update prices
      const compareElement = priceDiv.querySelector('s');
      if (compareElement) {
        compareElement.textContent = formattedComparePrice || '';
        compareElement.style.display = formattedComparePrice ? '' : 'none';
      }

      // Update current price (find the text node after the compare price)
      const priceNodes = Array.from(priceDiv.childNodes);
      const priceTextNode = priceNodes.find((node) => node.nodeType === Node.TEXT_NODE && node.textContent.trim());
      if (priceTextNode) {
        priceTextNode.textContent = ` ${formattedPrice} `;
      }

      // Update savings
      const savingsSpan = priceDiv.querySelector('.caption');
      if (savingsSpan && (displayComparePrice || bundleSavings)) {
        let savings;
        if (bundleSavings) {
          // Use bundle savings amount
          savings = Math.floor(bundleSavings / 100);
        } else if (displayComparePrice) {
          // Use compare at price savings
          savings = Math.floor((displayComparePrice - displayPrice) / 100);
        }

        if (savings > 0) {
          savingsSpan.textContent = `Save $${savings}`;
          savingsSpan.style.display = '';
        } else {
          savingsSpan.style.display = 'none';
        }
      } else if (savingsSpan) {
        savingsSpan.style.display = 'none';
      }
    }
  }

  /**
   * @method updatePrimaryPriceFromFetch
   * @private
   * @description Fetches product data and updates the primary price display
   */
  updatePrimaryPriceFromFetch() {
    const productHandle = window.location.pathname.match(/\/products\/([^\/]+)/)?.[1];
    const selectedOption = this.primaryVariantSelect.querySelector('input[type="radio"]:checked');
    const selectedValue = selectedOption?.value;

    if (productHandle && selectedValue) {
      // Fetch both product and metafields data
      Promise.all([
        fetch(`/products/${productHandle}.js`).then((response) => response.json()),
        fetch(`/products/${productHandle}?view=metafields`)
          .then((response) => response.json())
          .catch(() => null),
      ])
        .then(([product, metafields]) => {
          const selectedVariant = product.variants.find((v) => v.option1 === selectedValue);
          if (selectedVariant) {
            this.updatePrimaryPrice(product, selectedVariant, '[data-price-container]', metafields);
          }
        })
        .catch((error) => console.error('Error fetching product data:', error));
    }
  }

  /**
   * @method updateFinancingWidget
   * @private
   * @description Updates the financing widget by fetching the full product page
   */
  updateFinancingWidget() {
    const productHandle = window.location.pathname.match(/\/products\/([^\/]+)/)?.[1];
    if (!productHandle) return;

    // Get current variant selections to build the request URL
    const selectedOption = this.primaryVariantSelect?.querySelector('input[type="radio"]:checked');
    const selectedValue = selectedOption?.value;

    if (!selectedValue) return;

    // Find the ProductInfo section ID where the financing widget is located
    const productInfoSection = this.bundleForm.querySelector('[id^="ProductInfo-"]');
    if (!productInfoSection) return;

    const productInfoSectionId = productInfoSection.id.replace('ProductInfo-', '');

    // First fetch the product data to get the variant ID
    fetch(`/products/${productHandle}.js`)
      .then((response) => response.json())
      .then((product) => {
        // Find the variant that matches the selected option value (option1)
        const selectedVariant = product.variants.find((variant) => variant.option1 === selectedValue);

        if (!selectedVariant) {
          console.error('Variant not found for option:', selectedValue);
          return;
        }

        // Fetch the full product page with the variant parameter
        const requestUrl = `/products/${productHandle}?variant=${selectedVariant.id}`;

        return fetch(requestUrl);
      })
      .then((response) => {
        if (!response) return;
        return response.text();
      })
      .then((responseText) => {
        if (!responseText) return;

        const html = new DOMParser().parseFromString(responseText, 'text/html');

        // Find the financing widget in the returned HTML using the ProductInfo section ID
        const sourceFinancing = html.getElementById(`financing-${productInfoSectionId}`);
        const destinationFinancing = document.getElementById(`financing-${productInfoSectionId}`);

        if (sourceFinancing && destinationFinancing) {
          destinationFinancing.innerHTML = sourceFinancing.innerHTML;
          destinationFinancing.classList.toggle('hidden', sourceFinancing.classList.contains('hidden'));
        }
      })
      .catch((error) => {
        console.error('Error updating financing widget:', error);
      });
  }

  syncVariantSelections(event) {
    const selectedOption = event.target.value;
    const selectedOptionName = event.target.closest('[data-option-name]').dataset.optionName;

    const primaryFieldset = event.target.closest('fieldset[data-bundle-primary-select]');
    if (!primaryFieldset) return;
    const priceParent = document.querySelector(`[data-price-parent]`);

    // Update price for primary variant
    if (!priceParent) {
      this.updatePrimaryPriceFromFetch();
    }

    // Update financing widget
    this.updateFinancingWidget();

    // Update selected value text in primary fieldset
    const selectedValueSpan = primaryFieldset.querySelector('[data-selected-value]');
    if (selectedValueSpan) {
      selectedValueSpan.textContent = selectedOption;
    }

    const bundleContainers = this.bundleForm.querySelectorAll('[data-bundle-product-container]');

    bundleContainers.forEach((container) => {
      const hiddenInputs = container.querySelectorAll('input[type="hidden"]');

      hiddenInputs.forEach((hiddenInput) => {
        const optionName = hiddenInput.dataset.optionName;

        if (!optionName) return;

        if (optionName === selectedOptionName) {
          let mappedValue = selectedOption;
          try {
            const mappingAttr = hiddenInput.dataset.variantMapping;
            if (mappingAttr) {
              const decodedMapping = mappingAttr.replace(/&quot;/g, '"');
              const variantMapping = JSON.parse(decodedMapping);
              const mappedValues = variantMapping[selectedOption] || [selectedOption];
              mappedValue = mappedValues.find((mapped) => mapped) || selectedOption;
            }
          } catch (e) {
            console.error('Error parsing variant mapping:', e);
          }

          hiddenInput.value = mappedValue;
          hiddenInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });

      // Handle option 3 mapping based on primary selection
      const option3Input = container.querySelector('input[data-variant-option="option3"]');
      if (option3Input && option3Input.dataset.defaultOption3Mapping) {
        try {
          const decodedOption3Mapping = option3Input.dataset.defaultOption3Mapping.replace(/&quot;/g, '"');
          const option3Mapping = JSON.parse(decodedOption3Mapping);
          const mappedOption3Value = option3Mapping[selectedOption];

          if (mappedOption3Value) {
            // Handle array format like the existing mapping
            const finalOption3Value = Array.isArray(mappedOption3Value)
              ? mappedOption3Value.find((mapped) => mapped) || mappedOption3Value[0]
              : mappedOption3Value;

            option3Input.value = finalOption3Value;
            option3Input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        } catch (e) {
          console.error('Error parsing option 3 mapping:', e);
        }
      }

      this.updateAvailableOptions(container);
    });
  }

  /**
   * @method updateAvailableOptions
   * @private
   * @param {HTMLElement} container - The product container to update
   */
  updateAvailableOptions(container) {
    // Get product data
    const productInput = container.querySelector('[data-bundle-form-input]:not([data-bundle-form-input=""])');
    if (!productInput) return;

    const productId = productInput.getAttribute('data-bundle-form-input');
    const productJson = document.querySelector(`script[data-product-json-${productId}]`);
    if (!productJson) return;

    try {
      const product = JSON.parse(productJson.textContent);

      // Get current selections for this product
      const currentSelections = {};
      container.querySelectorAll('input[type="radio"]:checked, input[type="hidden"]').forEach((input) => {
        currentSelections[input.dataset.optionName] = input.value;
      });

      // Update available options (swatches)
      const swatchFieldsets = container.querySelectorAll('fieldset[data-option-name]');
      swatchFieldsets.forEach((fieldset) => {
        const optionName = fieldset.dataset.optionName;

        const swatchInputs = fieldset.querySelectorAll('input[type="radio"]');
        swatchInputs.forEach((input) => {
          const swatchValue = input.value;

          // Check if this swatch value is available with current selections
          const isAvailable = product.variants.some((variant) => {
            const variantMatch = Object.entries(currentSelections).every(([option, value]) => {
              // Skip the current option we're checking
              if (option === optionName) {
                const optionIndex = product.options.indexOf(option) + 1;
                const matches = variant[`option${optionIndex}`] === swatchValue;

                return matches;
              }
              // Check other options
              const optionIndex = product.options.indexOf(option) + 1;
              return variant[`option${optionIndex}`] === value;
            });

            return variantMatch && variant.available;
          });

          // Update swatch visibility and availability
          const label = input.parentElement.querySelector(`label[for="${input.id}"]`);
          if (label) {
            // Don't hide the swatch, just disable it and update classes
            input.disabled = !isAvailable;
            const swatchContainer = input.parentElement;
            if (swatchContainer) {
              swatchContainer.classList.remove('hidden'); // Remove hidden class
              swatchContainer.classList.toggle('unavailable', !isAvailable);
            }

            // Update the swatch element itself
            const swatchElement = label.querySelector('.swatch');
            if (swatchElement) {
              swatchElement.classList.toggle('unavailable', !isAvailable);
            }
          }
        });

        // Second pass: Check if selected option is unavailable and update if needed
        const selectedInput = fieldset.querySelector('input[type="radio"]:checked');
        if (selectedInput && selectedInput.disabled) {
          // Find first available option
          const firstAvailableInput = fieldset.querySelector('input[type="radio"]:not(:disabled)');
          if (firstAvailableInput) {
            firstAvailableInput.checked = true;
            firstAvailableInput.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      });
    } catch (e) {
      console.error('Error updating available options:', e);
    }
  }

  updateVariantSelections(event) {
    // Check if the parent fieldset has the primary select attribute
    const isPrimarySelect = event.target.dataset.bundlePrimarySelect !== undefined;

    if (isPrimarySelect) {
      // Update all products - use containers to avoid duplicates
      const containers = document.querySelectorAll('[data-bundle-product-container]');
      containers.forEach((container) => {
        // Get productId from the primary element (fieldset or radio)
        const fieldset = container.querySelector('fieldset[data-bundle-form-input]');
        const radio = container.querySelector('input[type="radio"][data-bundle-form-input]');
        const primaryInput = fieldset || radio;

        if (primaryInput) {
          const productId = primaryInput.getAttribute('data-bundle-form-input');
          if (productId) {
            // Skip if empty
            this.updateProductVariant(productId, container);
          }
        }
      });
      // Update the header savings based on mapping
      const headerSavings = document.querySelector('[data-savings-mapping]');
      if (headerSavings) {
        const savingsMapping = JSON.parse(headerSavings.dataset.savingsMapping);
        const selectedOption = this.primaryVariantSelect.querySelector('input[type="radio"]:checked');
        const selectedOptionName = selectedOption.value;
        const savings = savingsMapping[selectedOptionName];
        const savingsSpan = headerSavings.querySelector('s');
        savingsSpan.textContent = `$${savings / 100}`;
      }
      // Update financing widget after price changes
      this.updateFinancingWidget();
    } else {
      // Single product update
      const productId = event.target.closest('[data-bundle-form-input]').getAttribute('data-bundle-form-input');
      this.updateProductVariant(productId);
      // Update financing widget after price changes
      this.updateFinancingWidget();
    }
  }

  /**
   * @method updateCheckboxText
   * @private
   * @description Updates the upgrade/free text for a product checkbox
   * @param {string} productId - The product ID
   * @param {number} discountedPrice - The discounted price in cents
   */
  updateCheckboxText(productId, discountedPrice, container = null) {
    const scope = container || document;
    const checkboxContainer = scope
      .querySelector(`[data-bundle-product-container] input[value="${productId}"]`)
      ?.closest('[data-bundle-product-container]')
      ?.querySelector('[data-checkbox-container]');

    if (!checkboxContainer) return;

    const upgradeAmountElement = checkboxContainer.querySelector('[data-upgrade-amount-value]');
    if (!upgradeAmountElement) return;

    const upgradeTextElement = upgradeAmountElement.parentElement;
    if (!upgradeTextElement) return;

    if (discountedPrice > 0) {
      // Format as money without cents if it's a whole dollar amount
      const upgradeAmount = discountedPrice / 100;
      const formattedUpgrade =
        upgradeAmount % 1 === 0 ? `+$${upgradeAmount.toFixed(0)}` : `+$${upgradeAmount.toFixed(2)}`;
      upgradeAmountElement.textContent = formattedUpgrade;

      // Update the parent text using data attributes from container
      if (
        checkboxContainer.dataset.freeText &&
        upgradeTextElement.textContent.includes(checkboxContainer.dataset.freeText)
      ) {
        upgradeTextElement.innerHTML = `${checkboxContainer.dataset.upgradeText} <span data-upgrade-amount-value>${formattedUpgrade}</span>`;
      }
    } else {
      // Show free text when discounted price is 0
      if (checkboxContainer.dataset.freeText) {
        upgradeTextElement.textContent = checkboxContainer.dataset.freeText;
      }
    }
  }

  // New helper method to handle individual product updates
  updateProductVariant(productId, container = null) {
    const scope = container || document;
    const variantImage = scope.querySelector(`[data-bundle-variant-image-${productId}]`);
    const productJson = document.querySelector(`script[data-product-json-${productId}]`);
    const metafieldsJson = document.querySelector(`script[data-product-metafields-json-${productId}]`);
    const productIdClean = productId.split('-')[0];
    const sectionId = productId.split('-')[1];
    const priceParent = scope.querySelector(`[data-price-parent-id="${productIdClean}"]`);

    // Update main bundle price with price parent
    if (priceParent) {
      const priceParentId = priceParent.dataset.priceParentId;
      const priceParentSectionId = priceParentId + '-' + sectionId;
      const priceParentJson = scope.querySelector(`script[data-product-json-${priceParentSectionId}]`);
      const priceParentMetafieldsJson = scope.querySelector(
        `script[data-product-metafields-json-${priceParentSectionId}]`
      );
      const selectedVariant = this.findSelectedVariant(
        JSON.parse(priceParentJson.textContent),
        priceParentSectionId,
        priceParent
      );
      const metafields = priceParentMetafieldsJson ? JSON.parse(priceParentMetafieldsJson.textContent) : null;
      // Update the price of the parent bundle
      this.updatePrimaryPrice(
        JSON.parse(priceParentJson.textContent),
        selectedVariant,
        '[data-price-container]',
        metafields
      );
    }

    if (productJson) {
      const product = JSON.parse(productJson.textContent);
      const selectedVariant = this.findSelectedVariant(product, productId, container);
      const metafields = metafieldsJson ? JSON.parse(metafieldsJson.textContent) : null;

      if (variantImage) {
        if (selectedVariant?.featured_image?.src) {
          // Check if we have a cached image
          const cachedImage = this.variantImagesCache.get(selectedVariant.id);
          if (cachedImage) {
            variantImage.src = cachedImage.src;
          } else {
            // Fallback to regular loading if cache miss
            variantImage.setAttribute('src', `${selectedVariant.featured_image.src}&width=212`);
          }
          variantImage.setAttribute('alt', selectedVariant.featured_image.alt || '');
        }
      }
      // Update variant description if it exists
      const descriptionContainer = scope.querySelector(`[data-bundle-form-input="${productId}"]`)?.closest('.flex-col');
      const variantDescription = descriptionContainer?.querySelector('[data-selected-variant-description]');
      if (
        variantDescription &&
        metafields?.variants?.[selectedVariant.id]?.metafields?.custom?.set_description?.value
      ) {
        variantDescription.textContent =
          metafields.variants[selectedVariant.id].metafields.custom.set_description.value;
      }

      // Update selected value text in the fieldset
      const fieldset = scope.querySelector(`fieldset[data-bundle-form-input="${productId}"]`);
      if (fieldset) {
        const selectedValueSpan = fieldset.querySelector('[data-selected-value]');
        if (selectedValueSpan) {
          // Get the option name from the fieldset
          const optionName = fieldset.dataset.optionName;
          if (optionName) {
            // Find the option index in the product's options array
            const optionIndex = product.options.indexOf(optionName);
            if (optionIndex !== -1) {
              // Get the selected option value dynamically
              const selectedOption = selectedVariant[`option${optionIndex + 1}`];
              selectedValueSpan.textContent = selectedOption;
            }
          }
        }
      }

      // Update price for variant if it exists
      const priceContainer = scope.querySelector(`[data-price-container="${productId}"]`);
      if (priceContainer) {
        // Check for bundle savings
        const bundleSavings = this.getBundleSavings(selectedVariant, metafields);

        const priceDiv = priceContainer.querySelector('[data-before-price]');
        const saveAmount = priceContainer.querySelector('[data-save-amount-value]');
        if (priceDiv) {
          // Calculate display price
          const originalPrice = selectedVariant.price;
          const displayPrice = bundleSavings ? Math.max(0, originalPrice - bundleSavings) : originalPrice;

          // Format price with proper decimal places
          priceDiv.textContent = `$${(displayPrice / 100).toFixed(2)}`;

          if (bundleSavings) {
            // Show bundle savings
            saveAmount.textContent = `$${(bundleSavings / 100).toFixed(2)}`;
          } else if (priceContainer.dataset.promotionType === 'percent' && priceContainer.dataset.promotionPercent) {
            // Calculate percentage-based discount
            const promotionPercent = parseFloat(priceContainer.dataset.promotionPercent);
            const discountAmount = Math.round((selectedVariant.price * promotionPercent) / 100);
            const discountedPrice = Math.max(0, selectedVariant.price - discountAmount);

            // Update the discounted price display
            const discountedPriceElement = priceContainer.querySelector('.text-utility:not(.line-through)');
            if (discountedPriceElement) {
              discountedPriceElement.textContent = `$${(discountedPrice / 100).toFixed(2)}`;
            }

            // Update savings amount
            saveAmount.textContent = `$${(discountAmount / 100).toFixed(2)}`;

            // Update upgrade amount
            this.updateCheckboxText(productId, discountedPrice, container);

            // Update the data attribute for any other calculations
            priceContainer.dataset.priceAfterDiscount = discountedPrice;
          } else if (priceContainer.dataset.priceAfterDiscount) {
            // Format savings amount with proper decimal places (existing fixed amount logic)
            saveAmount.textContent = `$${(
              (selectedVariant.price - priceContainer.dataset.priceAfterDiscount) /
              100
            ).toFixed(2)}`;

            // Update upgrade amount for fixed amount discounts
            const discountedPrice = parseInt(priceContainer.dataset.priceAfterDiscount);
            this.updateCheckboxText(productId, discountedPrice, container);
          }
        }
      }
    }
  }

  // Check if the input is either a fieldset or an input element
  findSelectedVariant(product, productId, container = null) {
    const options = [];
    const scope = container || document;

    const radioInputs = scope.querySelectorAll(`fieldset[data-bundle-form-input="${productId}"] input[type="radio"]`);
    radioInputs.forEach((input) => {
      if (!input.checked) return;
      const optionName = input.getAttribute('data-option-name');
      if (optionName) {
        const optionIndex = product.options.indexOf(optionName);
        if (optionIndex !== -1) {
          options[optionIndex] = input.value;
        }
      }
    });

    const hiddenInputs = scope.querySelectorAll(`[data-bundle-form-hidden-input="${productId}"]`);
    hiddenInputs.forEach((input) => {
      const name = input.dataset.variantOption;
      const optionMatch = name.match(/option(\d+)/);
      if (optionMatch) {
        const optionIndex = parseInt(optionMatch[1]) - 1;
        // The hidden input value already contains the mapped value from syncVariantSelections
        options[optionIndex] = input.value;
      }
    });

    // Find and return the variant that matches all options
    return product.variants.find((variant) =>
      options.every((option, index) => variant[`option${index + 1}`] === option)
    );
  }

  getSelectedProducts(parentProductData = null) {
    const products = [];

    // Add parent product if provided
    if (parentProductData && parentProductData.parentProduct && parentProductData.selectedVariant) {
      const selectedVariantId = parseInt(parentProductData.selectedVariant.id);

      if (selectedVariantId) {
        products.push({ id: selectedVariantId, quantity: 1 });
      }
    }

    // Get single product variants (non-checkbox-group products)
    const singleVariants = document.querySelectorAll('[data-bundle-form-input]');
    singleVariants.forEach((variantContainer) => {
      // Skip if this is within a checkbox group container
      if (variantContainer.closest('[data-bundle-checkbox-group]')) {
        return;
      }

      const productId = variantContainer.getAttribute('data-bundle-form-input');
      const productJson = document.querySelector(`script[data-product-json-${productId}]`);
      if (!productJson) return;

      const container = variantContainer.closest('[data-bundle-product-container]');
      const product = JSON.parse(productJson.textContent);
      const selectedVariant = this.findSelectedVariant(product, productId, container);
      if (selectedVariant?.id) {
        products.push({ id: selectedVariant.id, quantity: 1 });
      }
    });

    // Get selected products from all checkbox groups
    const checkboxGroups = document.querySelectorAll('[data-bundle-checkbox-group]');
    checkboxGroups.forEach((group) => {
      const selectedInput = group.querySelector('input[type="radio"]:checked, input[type="checkbox"]:checked');
      if (selectedInput) {
        const productId = selectedInput.value;
        const productJson = document.querySelector(`script[data-product-json-${productId}]`);

        if (productJson) {
          const product = JSON.parse(productJson.textContent);
          const selectedVariant = this.findSelectedVariant(product, productId, group);
          if (selectedVariant?.id) {
            products.push({ id: selectedVariant.id, quantity: 1 });
          }
        }
      }
    });

    return products;
  }

  async handleAddToCart(event) {
    event.preventDefault();

    // Check if all required groups have a selection
    const checkboxGroups = document.querySelectorAll('[data-bundle-checkbox-group]');
    const unselectedGroups = [];

    checkboxGroups.forEach((group) => {
      const selectedInput = group.querySelector('input[type="radio"]:checked, input[type="checkbox"]:checked');

      // Check if this group has the data-group-name attribute (required group)
      const hasGroupName = group.hasAttribute('data-group-name');

      if (!selectedInput) {
        if (hasGroupName) {
          // This is a required group - get the group name for error messaging
          const groupName = group.dataset.groupName || 'product';
          unselectedGroups.push(groupName);
        }
        // If no data-group-name, this is a radio selection group and only one selection is required
        // across all such groups, so we don't add it to unselectedGroups
      }
    });

    if (unselectedGroups.length > 0) {
      let errorMessage;
      if (unselectedGroups.length === 1) {
        errorMessage = `Please select your ${unselectedGroups[0]}`;
      } else if (unselectedGroups.length === 2) {
        errorMessage = `Please select your ${unselectedGroups[0]} and your ${unselectedGroups[1]}`;
      } else {
        const lastGroup = unselectedGroups.pop();
        errorMessage = `Please select your ${unselectedGroups.join(', ')}, and your ${lastGroup}`;
      }
      this.showError(errorMessage);
      return;
    }

    // Group radio groups by block ID and check each block separately
    const radioGroupsByBlock = {};
    const radioGroups = document.querySelectorAll('[data-bundle-checkbox-group]:not([data-group-name])');

    radioGroups.forEach((group) => {
      const blockId = group.closest('[data-block-id]')?.dataset.blockId;
      if (blockId) {
        if (!radioGroupsByBlock[blockId]) {
          radioGroupsByBlock[blockId] = [];
        }
        radioGroupsByBlock[blockId].push(group);
      }
    });

    // Check each block separately - each block needs at least one selection
    const unselectedBlocks = [];
    Object.entries(radioGroupsByBlock).forEach(([blockId, groups]) => {
      // Check if ANY group in this block has a selection
      const hasSelection = groups.some((group) =>
        group.querySelector('input[type="radio"]:checked, input[type="checkbox"]:checked')
      );

      if (!hasSelection) {
        // Get item titles from the h6 headings in each product group
        const itemTitles = [];
        groups.forEach((group) => {
          const heading = group.querySelector('h6.text-h6')?.textContent?.trim();
          if (heading && !itemTitles.includes(heading)) {
            itemTitles.push(heading);
          }
        });

        let blockName;
        if (itemTitles.length === 1) {
          blockName = itemTitles[0];
        } else if (itemTitles.length === 2) {
          blockName = `${itemTitles[0]} or ${itemTitles[1]}`;
        } else if (itemTitles.length > 2) {
          const lastItem = itemTitles.pop();
          blockName = `${itemTitles.join(', ')}, or ${lastItem}`;
        } else {
          // Fallback to block heading if no product titles found
          const blockContainer = document.querySelector(`[data-block-id="${blockId}"]`);
          blockName = blockContainer?.querySelector('h6')?.textContent?.trim() || 'selection';
        }

        unselectedBlocks.push(blockName);
      }
    });

    if (unselectedBlocks.length > 0) {
      let errorMessage;
      if (unselectedBlocks.length === 1) {
        errorMessage = `Please select your ${unselectedBlocks[0]}`;
      } else if (unselectedBlocks.length === 2) {
        errorMessage = `Please select your ${unselectedBlocks[0]} and your ${unselectedBlocks[1]}`;
      } else {
        const lastBlock = unselectedBlocks.pop();
        errorMessage = `Please select your ${unselectedBlocks.join(', ')}, and your ${lastBlock}`;
      }
      this.showError(errorMessage);
      return;
    }

    // Check if all selected variants are in stock
    const outOfStockItems = [];
    checkboxGroups.forEach((group) => {
      const selectedInput = group.querySelector('input[type="radio"]:checked, input[type="checkbox"]:checked');
      if (selectedInput) {
        const productId = selectedInput.value;
        const productJson = document.querySelector(`script[data-product-json-${productId}]`);
        if (productJson) {
          const product = JSON.parse(productJson.textContent);
          const selectedVariant = this.findSelectedVariant(product, productId, group);
          if (!selectedVariant?.available) {
            outOfStockItems.push(selectedVariant.name);
          }
        }
      }
    });

    if (outOfStockItems.length > 0) {
      const errorMessage =
        outOfStockItems.length === 1
          ? ERRORS.SOLD_OUT(outOfStockItems[0])
          : `${outOfStockItems.join(', ')} are currently sold out, please choose other options`;
      this.showError(errorMessage);
      return;
    }

    const selectedProducts = this.getSelectedProducts(this.useParentProduct() ? this.getParentProduct() : null);

    if (selectedProducts.length === 0) {
      this.showError(ERRORS.NO_PRODUCTS);
      return;
    }

    // Get the bundle template value
    const bundleAtcElement = document.querySelector('[data-bundle-atc-template]');
    const bundleTemplate = bundleAtcElement?.getAttribute('data-bundle-atc-template') || '';

    // Get the current page's product handle and title
    const productHandle = window.location.pathname.match(/\/products\/([^\/]+)/)?.[1];
    let pageSource = 'bundle';

    // First check for data-bundle-template-name in the DOM
    const bundleTemplateElement = document.querySelector('[data-bundle-template-name]');
    const bundleTemplateName = bundleTemplateElement?.getAttribute('data-bundle-template-name');

    // Generate a robust unique bundle id
    const hasRandomUUID = typeof window.crypto !== 'undefined' && typeof window.crypto.randomUUID === 'function';
    const bundleId = hasRandomUUID
      ? window.crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

    if (bundleTemplateName) {
      pageSource = `bundle - ${bundleTemplateName}`;
    } else if (productHandle) {
      try {
        const response = await fetch(`/products/${productHandle}.js`);
        const product = await response.json();
        pageSource = `bundle - ${product.title}`;
      } catch (error) {
        console.error('Error fetching product data for page source:', error);
        // Fallback to just 'bundle' if fetch fails
      }
    }

    // Add _bundle property to each selected product
    const productsWithBundleProperty = selectedProducts.map((product) => ({
      ...product,
      properties: {
        ...product.properties,
        _bundle: bundleTemplate,
        _bundle_id: bundleId,
      },
    }));

    this.addToCartButton.classList.add('loading');

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: productsWithBundleProperty,
          attributes: {
            _page_source: pageSource,
          },
        }),
      });

      const result = await response.json();

      if (response.ok) {
        this.showSuccess();
      } else {
        this.showError(result.description || ERRORS.GENERAL_ERROR);
      }
    } catch (error) {
      this.showError(ERRORS.GENERAL_ERROR);
    } finally {
      this.addToCartButton.classList.remove('loading');
    }
  }

  showError(message) {
    const errorContainer = document.querySelector('[data-bundle-atc-error]');
    const errorMessage = document.querySelector('[data-bundle-atc-error-message]');
    const noStockError = document.querySelector('[data-bundle-atc-error-no-stock]');

    if (errorContainer && errorMessage) {
      errorContainer.classList.remove('hidden');

      // Show specific error message for out of stock cases
      if (message.includes('sold out')) {
        errorMessage.classList.add('hidden');
        noStockError.classList.remove('hidden');
        noStockError.textContent = message;
      } else {
        errorMessage.classList.remove('hidden');
        noStockError.classList.add('hidden');
        errorMessage.textContent = message;
      }

      setTimeout(() => {
        errorContainer.classList.add('hidden');
      }, TIMEOUTS.MESSAGE_DURATION);
    }
  }

  showSuccess() {
    this.addToCartText.classList.add('hidden');
    this.addToCartSuccess.classList.remove('hidden');

    setTimeout(() => {
      this.addToCartText.classList.remove('hidden');
      this.addToCartSuccess.classList.add('hidden');
    }, TIMEOUTS.MESSAGE_DURATION);
  }

  /**
   * @method prefetchVariantImages
   * @private
   */
  prefetchVariantImages() {
    // Gather all product JSONs
    const productJsons = document.querySelectorAll('script[data-product-json]');

    productJsons.forEach((json) => {
      try {
        const product = JSON.parse(json.textContent);

        // Queue all variant images for preloading
        product.variants.forEach((variant) => {
          if (variant.featured_image?.src) {
            const imageUrl = `${variant.featured_image.src}&width=212`;
            this.queueImagePreload(imageUrl, variant.id);
          }
        });

        // Start preloading process
        this.processImageQueue();
      } catch (e) {
        console.error('Error prefetching variant images:', e);
      }
    });
  }

  /**
   * @method queueImagePreload
   * @private
   */
  queueImagePreload(imageUrl, variantId) {
    this.imagePreloadQueue.push({ imageUrl, variantId });
  }

  /**
   * @method processImageQueue
   * @private
   */
  processImageQueue() {
    if (!this.imagePreloadQueue.length) return;

    const { imageUrl, variantId } = this.imagePreloadQueue.shift();

    const img = new Image();
    img.onload = () => {
      this.variantImagesCache.set(variantId, img);
      // Process next image in queue
      this.processImageQueue();
    };
    img.src = imageUrl;
  }
}

// Initialize the bundle functionality
customElements.define(
  'product-bundle',
  class ProductBundleElement extends HTMLElement {
    constructor() {
      super();
      this.bundle = null;
    }

    connectedCallback() {
      this.bundle = new ProductBundle();
    }

    disconnectedCallback() {
      this.bundle?.cleanup();
    }
  }
);
